import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Tag, ToggleLeft, ToggleRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { discountsAPI } from '../../services/api';

interface Discount {
  _id: string;
  code: string;
  name: string;
  description?: string;
  type: 'percentage' | 'fixed';
  value: number;
  minOrderAmount: number;
  maxDiscountAmount: number;
  usageLimit: number;
  usageCount: number;
  perCustomerLimit: number;
  startDate?: string;
  endDate?: string;
  isActive: boolean;
  applicableCategories: string[];
  createdAt: string;
}

interface DiscountFormData {
  code: string;
  name: string;
  description: string;
  type: 'percentage' | 'fixed';
  value: number;
  minOrderAmount: number;
  maxDiscountAmount: number;
  usageLimit: number;
  perCustomerLimit: number;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

const emptyForm: DiscountFormData = {
  code: '',
  name: '',
  description: '',
  type: 'percentage',
  value: 0,
  minOrderAmount: 0,
  maxDiscountAmount: 0,
  usageLimit: 0,
  perCustomerLimit: 0,
  startDate: '',
  endDate: '',
  isActive: true,
};

const DiscountManagement: React.FC = () => {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<Discount | null>(null);
  const [formData, setFormData] = useState<DiscountFormData>(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchDiscounts(); }, []);

  const fetchDiscounts = async () => {
    setLoading(true);
    try {
      const res = await discountsAPI.getDiscounts();
      setDiscounts(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error('Failed to load discounts');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingDiscount(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (d: Discount) => {
    setEditingDiscount(d);
    setFormData({
      code: d.code,
      name: d.name,
      description: d.description || '',
      type: d.type,
      value: d.value,
      minOrderAmount: d.minOrderAmount,
      maxDiscountAmount: d.maxDiscountAmount,
      usageLimit: d.usageLimit,
      perCustomerLimit: d.perCustomerLimit,
      startDate: d.startDate ? d.startDate.split('T')[0] : '',
      endDate: d.endDate ? d.endDate.split('T')[0] : '',
      isActive: d.isActive,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.code.trim() || !formData.name.trim() || formData.value <= 0) {
      toast.error('Code, name, and value are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
      };
      if (editingDiscount) {
        await discountsAPI.updateDiscount(editingDiscount._id, payload);
        toast.success('Discount updated');
      } else {
        await discountsAPI.createDiscount(payload);
        toast.success('Discount created');
      }
      setShowModal(false);
      fetchDiscounts();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to save discount');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this discount?')) return;
    try {
      await discountsAPI.deleteDiscount(id);
      toast.success('Discount deleted');
      fetchDiscounts();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const toggleActive = async (d: Discount) => {
    try {
      await discountsAPI.updateDiscount(d._id, { isActive: !d.isActive });
      fetchDiscounts();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-gray-500 hover:text-gray-700"><ArrowLeft className="h-5 w-5" /></Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Tag className="h-6 w-6 text-purple-500" /> Discounts
            </h1>
            <p className="text-sm text-gray-500 mt-1">Create and manage discount codes for your customers</p>
          </div>
        </div>
        <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700">
          <Plus className="h-4 w-4" /> New Discount
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valid</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {discounts.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-400">No discounts yet. Create one to get started.</td></tr>
              ) : discounts.map((d) => (
                <tr key={d._id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm font-mono font-bold text-primary-700 whitespace-nowrap">{d.code}</td>
                  <td className="px-6 py-3 text-sm text-gray-900">{d.name}</td>
                  <td className="px-6 py-3 text-sm text-gray-700 whitespace-nowrap">
                    {d.type === 'percentage' ? `${d.value}%` : `£${d.value.toFixed(2)}`}
                    {d.minOrderAmount > 0 && <span className="text-gray-400 ml-1">(min £{d.minOrderAmount.toFixed(2)})</span>}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {d.usageCount}{d.usageLimit > 0 ? `/${d.usageLimit}` : ''}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap">
                    {d.startDate ? new Date(d.startDate).toLocaleDateString() : '—'}
                    {' - '}
                    {d.endDate ? new Date(d.endDate).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <button onClick={() => toggleActive(d)} title={d.isActive ? 'Active' : 'Inactive'}>
                      {d.isActive
                        ? <ToggleRight className="h-6 w-6 text-green-500" />
                        : <ToggleLeft className="h-6 w-6 text-gray-400" />
                      }
                    </button>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-right">
                    <button onClick={() => openEdit(d)} className="text-primary-600 hover:text-primary-800 mr-3" title="Edit">
                      <Edit className="h-4 w-4 inline" />
                    </button>
                    <button onClick={() => handleDelete(d._id)} className="text-red-500 hover:text-red-700" title="Delete">
                      <Trash2 className="h-4 w-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[560px] max-w-[95vw] max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">{editingDiscount ? 'Edit Discount' : 'New Discount'}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700">Code</label>
                <input className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 uppercase" value={formData.code}
                  onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="e.g. SUMMER20" />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Name</label>
                <input className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Summer Sale" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-gray-700">Description (optional)</label>
                <input className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={formData.description}
                  onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Type</label>
                <select className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={formData.type}
                  onChange={(e) => setFormData(p => ({ ...p, type: e.target.value as any }))}>
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (£)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-700">Value</label>
                <input type="number" min={0} step={formData.type === 'percentage' ? 1 : 0.01}
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={formData.value}
                  onChange={(e) => setFormData(p => ({ ...p, value: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Min Order Amount (£)</label>
                <input type="number" min={0} step={0.01}
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={formData.minOrderAmount}
                  onChange={(e) => setFormData(p => ({ ...p, minOrderAmount: parseFloat(e.target.value) || 0 }))} />
                <p className="text-xs text-gray-400 mt-1">0 = no minimum</p>
              </div>
              <div>
                <label className="block text-sm text-gray-700">Max Discount (£)</label>
                <input type="number" min={0} step={0.01}
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={formData.maxDiscountAmount}
                  onChange={(e) => setFormData(p => ({ ...p, maxDiscountAmount: parseFloat(e.target.value) || 0 }))} />
                <p className="text-xs text-gray-400 mt-1">0 = no cap</p>
              </div>
              <div>
                <label className="block text-sm text-gray-700">Usage Limit (total)</label>
                <input type="number" min={0}
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={formData.usageLimit}
                  onChange={(e) => setFormData(p => ({ ...p, usageLimit: parseInt(e.target.value) || 0 }))} />
                <p className="text-xs text-gray-400 mt-1">0 = unlimited</p>
              </div>
              <div>
                <label className="block text-sm text-gray-700">Per Customer Limit</label>
                <input type="number" min={0}
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={formData.perCustomerLimit}
                  onChange={(e) => setFormData(p => ({ ...p, perCustomerLimit: parseInt(e.target.value) || 0 }))} />
                <p className="text-xs text-gray-400 mt-1">0 = unlimited</p>
              </div>
              <div>
                <label className="block text-sm text-gray-700">Start Date</label>
                <input type="date" className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={formData.startDate}
                  onChange={(e) => setFormData(p => ({ ...p, startDate: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm text-gray-700">End Date</label>
                <input type="date" className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" value={formData.endDate}
                  onChange={(e) => setFormData(p => ({ ...p, endDate: e.target.value }))} />
              </div>
              <div className="col-span-2">
                <label className="inline-flex items-center">
                  <input type="checkbox" checked={formData.isActive}
                    onChange={(e) => setFormData(p => ({ ...p, isActive: e.target.checked }))}
                    className="rounded text-primary-600 focus:ring-primary-500" />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={submitting}>
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountManagement;
